import React, { useState } from "react";
import {
  Table,
  Card,
  Spin,
  message,
  Button,
  Modal,
  Tooltip,
  Input,
  Select,
  Row,
  Col,
} from "antd";
import { useFetch } from "../../../Utils/useFetch";
import {
  FETCH_ALL_COUNTER_API,
  FETCH_ALL_DEPT_API,
  FETCH_ALL_USER_API,
} from "../../../Constants/Apis";
import UserCreationForm from "../../elements/Forms/UserCreationForm/UserCreationForm";
import { deleteUser } from "../../../Services/UserService";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

const { Search } = Input;
const { Option } = Select;

const UserListPage = ({ userData }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedCounter, setSelectedCounter] = useState(null);

  const {
    fetchedData: { data = [] },
    loading,
    callReFetch,
  } = useFetch(FETCH_ALL_USER_API);

  const {
    fetchedData: { data: DeptData },
  } = useFetch(`${FETCH_ALL_DEPT_API}`);

  const {
    fetchedData: { data: CounterData },
    callReFetch: callReFetchCounter,
  } = useFetch(
    // For arearep users, fetch all counters regardless of department
    // For other users, filter by selected department
    userData?.roles?.includes("arearep") 
      ? FETCH_ALL_COUNTER_API 
      : (selectedDept ? `${FETCH_ALL_COUNTER_API}/dept/${selectedDept}` : null)
  );

  const handleEdit = (user) => {
    setEditingUser(user);
    setSelectedDept(user.dept);
    setIsEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setEditingUser(null);
  };

  const handleDelete = async (user) => {
    Modal.confirm({
      title: "Delete User",
      content: `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteUser(user._id);
          message.success("User deleted successfully");
          callReFetch();
        } catch (error) {
          message.error("Failed to delete user");
        }
      },
    });
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleOpenModal = () => setIsModalVisible(true);
  const handleCloseModal = () => setIsModalVisible(false);

  const getUserCounterCodes = (user) => {
    const raw = user?.counterCode;
    if (Array.isArray(raw)) {
      return raw
        .map((c) => (typeof c === "string" ? c.split(",") : [c]))
        .flat()
        .map((c) => String(c).trim())
        .filter((c) => c.length > 0);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
    }
    return [];
  };

  const filteredData = data.filter((user) => {
    const matchesSearch =
      !searchKeyword ||
      user.username?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchKeyword.toLowerCase());

    const matchesCounter =
      !selectedCounter || getUserCounterCodes(user).includes(selectedCounter);

    const matchesInternalAdmin =
      !userData?.roles?.includes("internaladmin") ||
      user?.roles?.includes("spg") ||
      user?.roles?.includes("arearep");

    return matchesSearch && matchesCounter && matchesInternalAdmin;
  });

  const uniqueCounterCodes = Array.from(
    new Set(
      data
        .flatMap((item) => getUserCounterCodes(item))
        .filter((code) => code && code.length > 0)
    )
  ).sort((a, b) => a.localeCompare(b));

  const columns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Department",
      dataIndex: "dept",
      key: "dept",
    },
    {
      title: "Counter Code",
      dataIndex: "counterCode",
      key: "counterCode",
    },
    {
      title: "Status",
      dataIndex: "isDeleted",
      key: "status",
      render: (isDeleted) => (isDeleted ? "Inactive" : "Active"),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Tooltip title="Edit user">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {(userData?.roles?.includes("admin") ||
            userData?.roles?.includes("internaladmin")) && (
            <Tooltip title="Delete user">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <Card
        title="User Management"
        extra={
          <Row gutter={12}>
            <Col>
              <Search
                placeholder="Search by username or name"
                allowClear
                onSearch={(value) => setSearchKeyword(value)}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={{ width: 250 }}
              />
            </Col>
            <Col>
              <Select
                allowClear
                showSearch
                placeholder="Filter by counterCode"
                style={{ width: 200 }}
                onChange={(value) => setSelectedCounter(value)}
              >
                {uniqueCounterCodes.map((code) => (
                  <Option key={code} value={code}>
                    {code}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Button type="primary" onClick={handleOpenModal}>
                Create New User
              </Button>
            </Col>
          </Row>
        }
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="_id"
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: true }}
          />
        )}
      </Card>

      <Modal
        title="Create User"
        open={isModalVisible}
        footer={null}
        onCancel={handleCloseModal}
      >
        <UserCreationForm
          callReFetch={callReFetch}
          onClose={handleCloseModal}
          DeptData={DeptData}
          userData={userData}
          CounterData={CounterData}
          setSelectedDept={setSelectedDept}
          callReFetchCounter={callReFetchCounter}
        />
      </Modal>

      <Modal
        title="Edit User"
        open={isEditModalVisible}
        footer={null}
        onCancel={handleCloseEditModal}
      >
        <UserCreationForm
          callReFetch={callReFetch}
          onClose={handleCloseEditModal}
          DeptData={DeptData}
          userData={userData}
          CounterData={CounterData}
          setSelectedDept={setSelectedDept}
          callReFetchCounter={callReFetchCounter}
          initialValues={editingUser}
          isEditMode={true}
        />
      </Modal>
    </div>
  );
};

export default UserListPage;
